import { Table, Tag, Space, message } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { UseMutateAsyncFunction, useMutation, useQuery } from "react-query";
import { deleteItemUnit, getItemUnitById } from "../../../api/itemUnit";
import { AddEditItemUnit } from "./AddEditItemUnit";
import { ItemUnitType } from "./unitType";
import EditButton from "../../UI/EditButton/editbutton";
import DeleteButton from "../../UI/DeleteButton/deletebutton";
import DeleteModal from "../../UI/ModalComponent/DeleteModal/deletemodal";
import { getItemType } from "../../../api/type";
import { AxiosResponse } from "axios";

interface inventoryProps {
  isItemUnitLoading: boolean;
  paginatedItemUnit: any;
  ItemUnitMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
      isActive?: boolean | undefined;
    },
    unknown
  >;
  setPaginatedItemUnit: Dispatch<any>;
  filter: boolean | undefined;
  search: string;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  tableKey: string;
}
interface ItemUnitInterface {
  id: number;
  name: string;
  abbreviation: string;
  isActive: boolean;
  itemType: string;
}
const ItemUnitList = ({
  isItemUnitLoading,
  paginatedItemUnit,
  ItemUnitMutation,
  setPaginatedItemUnit,
  filter,
  search,
  page,
  setPage,
  tableKey,
}: inventoryProps) => {
  const [t] = useTranslation("common");
  const [selectedUnit, setSelectedUnit] = useState<any>();
  const [openUnit, setOpenUnit] = useState(false);
  const [Unit, setUnit] = useState<ItemUnitType>();
  const [prevPage, setPrevPage] = useState<number>(0);
  const [, setError] = useState("");
  const [maxPage, setMaxPage] = useState<number>(0);

  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      ItemUnitMutation({
        limit: page * 8 + 8,
        q: search,
        isActive: filter,
      }).then((res) => {
        if (page > maxPage) setMaxPage(page);
        setPaginatedItemUnit(res.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  const getItemUnitMutation = useMutation("getItemUnit", getItemUnitById);
  function fillItemUnit(id: any) {
    getItemUnitMutation.mutate(id, {
      onSuccess: (res) => {
        setUnit(res.data);
        setOpenUnit(true);
      },
    });
  }

  const onError = (error: any, context: unknown) => {
    if (setError) {
      setError(error.response.data.message);
      message.error({
        content: error.response.data.message,
        duration: 10,
        onClick: () => {
          message.destroy();
        },
      });
    }
  };

  const deleteItemUnitMutation = useMutation("deleteItemUnit", deleteItemUnit, {
    onSuccess: () => {
      ItemUnitMutation({
        limit: page * 8 + 8,
        q: search,
        isActive: filter,
      }).then((res: any) => {
        setPaginatedItemUnit(res.data);
        message.success("Complete successfully");
      });
    },
    onError,
  });

  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);
  const [nameToDelete, setNameToDelete] = useState<any>();

  const DeleteItemUnit = (id: string | undefined, name: string): void => {
    setOpen(true);
    setIdToDelete(id);
    setNameToDelete(name);
  };

  const handleOk = () => {
    if (idToDelete) {
      deleteItemUnitMutation.mutate({ id: idToDelete });
    }
    setOpen(false);
  };

  const [formatedItem, setFormatedItem] = useState<
    { value: string; label: string }[]
  >([]);
  const { isLoading: loadingItem } = useQuery("getItemType", getItemType, {
    refetchOnWindowFocus: false,
    onSuccess: (res) => {
      setFormatedItem(
        res.data.map((itemTypeId: any) => {
          return { value: itemTypeId.id, label: itemTypeId.name };
        })
      );
    },
  });

  const columns: ColumnsType<ItemUnitInterface> = [
    {
      title: `${t("unit-type")}`,
      dataIndex: "itemType",
      key: "itemType",
      width: "10rem",
      className: "table-header",
      render: (record) => {
        return record?.name;
      },
    },
    {
      title: `${t("unit-name")}`,
      dataIndex: "name",
      key: "name",
    },
    {
      title: `${t("abbreviation")}`,
      dataIndex: "abbreviation",
      key: "abbreviation",
      width: "12rem",
    },
    {
      title: `${t("status")}`,
      dataIndex: "isActive",
      align: "center",
      width: "5rem",
      key: "isActive",
      render: (record: any) =>
        record ? (
          <Tag className="active">Active</Tag>
        ) : (
          <Tag className="inactive">Inactive</Tag>
        ),
    },
    {
      title: `${t("action")}`,
      key: "action",
      align: "center",
      fixed: "right",
      width: "5rem",
      render: (record: any) => (
        <Space>
          <EditButton
            onClick={() => {
              setSelectedUnit(record.id);
              fillItemUnit(record.id);
            }}
          />
          <DeleteButton
            onClick={() => DeleteItemUnit(record?.id, record?.name)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <DeleteModal
        open={open}
        setOpen={setOpen}
        name={nameToDelete}
        onOk={handleOk}
      />
      {openUnit && Unit && (
        <AddEditItemUnit
          Unit={Unit}
          openUnit={openUnit}
          setOpenUnit={setOpenUnit}
          id={selectedUnit}
          formatedItem={formatedItem}
          loadingItem={loadingItem}
          ItemUnitMutation={ItemUnitMutation}
          setPaginatedItemUnit={setPaginatedItemUnit}
          filter={filter}
          search={search}
        />
      )}

      <Table
        key={tableKey}
        scroll={{ x: 1000 }}
        columns={columns}
        pagination={{ pageSize: 8 }}
        dataSource={paginatedItemUnit}
        loading={isItemUnitLoading}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
        rowKey={(record) => record.id}
      />
    </>
  );
};

export default ItemUnitList;
